<template>
  <div class="wt-editor-top-bar-inner">
    <template v-if="shouldShow">
      <div v-if="overlayTracks.length" class="-left-side">
        <span class="wt-fragment-duration">
          <span class="wticon wticon-clock3" />
          <span class="-duration" v-text="displayDuration" />
        </span>
        <div v-if="showError" class="-error">
          <span class="wt2-color-error wt2-text-s"
            >Warning: slow motion video will be cut</span
          >
        </div>
        <template v-if="isVideoFragment">
          <wt-dropdown
            :item-height="40"
            :disabled="true"
            type="normal fixed narrow"
            :items="paceItems"
            :on-click-item="onChangePace"
            :current-item-id="playbackRate"
            :show-triangle="true"
            label="Speed"
          />
          <i class="wticon wticon-information1" v-tooltip="speedTooltip" />
        </template>
      </div>
      <div v-else class="-left-side -set-duration">
        <span class="-label">Set Duration:</span>
        <wt-duration
          :min="assetLimit.min * 1e3"
          :max="assetLimit.max * 1e3"
          :seconds="assetTimes.displayDuration"
          @change="changeDuration"
        />
        <wt-dropdown
          v-if="isVideoFragment"
          class="-pace-dropdown"
          :item-height="40"
          type="normal fixed narrow"
          :items="paceItems"
          :on-click-item="onChangePace"
          :current-item-id="playbackRate"
          :show-triangle="true"
          label="Speed"
        />
      </div>

      <div class="-right-side">
        <wt-video-poster-toggle :asset="workModel" />
        <div v-if="showPingPongToggle" class="-toggle-ping-pong">
          <span>Ping Pong GIF</span>
          <wt-toggle
            v-model="hasPingPong"
            :disabled="overlayTracks.length > 0"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
/** (GW)
 * Asset Editor's Fragment Details Vue (bottom bar)
 */
import app from 'app';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import WtVideoPosterToggle from 'AssetEditor/functionality/Poster/wtVideoPosterToggle.vue';

export default {
  components: { WtVideoPosterToggle },
  analytics: {
    'assetEditor.pace': 'Changed Pacing',
  },

  data() {
    return {
      showError: false,
    };
  },

  computed: {
    ...mapGetters('assetEditor', [
      'workModel',
      'assetModel',
      'story',
      'currentPage',
      'AGD',
      'activePages',
    ]),
    ...mapState('assetEditor', ['initialModel']),

    hasPingPong: {
      get() {
        return !!this.workModel.agd.pingPongGif;
      },
      set(isOn) {
        this.setAGD({
          pingPongGif: isOn,
        });
        this.generateRTPByWorkModel();
      },
    },

    overlayTracks() {
      return this.workModel.overlayTracks || [];
    },

    showPingPongToggle() {
      return app.assetType.isGIF(this.initialModel);
    },

    displayDuration() {
      return this.getDisplayDuration(this.workModel);
    },

    shouldShow() {
      return this.currentPage.id === 'preview';
    },

    isVideoFragment() {
      return (
        app.assetType.isFRAGMENT(this.workModel) ||
        app.assetType.isSOCIALFRAGMENT(this.workModel)
      );
    },

    paceItems() {
      return [
        { id: 0.25, label: 'X0.25' },
        { id: 0.5, label: 'X0.5' },
        { id: 1, label: 'Normal' },
        { id: 2, label: 'X2' },
        { id: 4, label: 'X4' },
      ];
    },

    playbackRate() {
      return this.AGD.videoSpeed || 1;
    },

    assetLimit() {
      return {
        min: Math.min(this.workModel.limit.min, this.workModel.limit.min),
        max: this.workModel.limit.max / this.playbackRate,
      };
    },

    assetTimes() {
      return _.pick(
        this.workModel,
        'startTime',
        'endTime',
        'displayStartTime',
        'displayDuration'
      );
    },

    storyBgMusic() {
      return this.story.bgMusic;
    },

    speedTooltip() {
      return {
        title: 'Changing speed is not supported for clips containing collage',
        placement: 'bottom',
      };
    },
  },

  methods: {
    ...mapActions('assetEditor', ['generateRTPByWorkModel']),
    ...mapMutations('assetEditor', ['setAGD', 'setNewDuration', 'setTimes']),

    onChangePace(paceValue) {
      this.setAGD({
        videoSpeed: paceValue,
      });

      const times = this.assetTimes;
      const startTime = times.startTime;
      const endTime = times.endTime;
      const newDuration = (endTime - startTime) / (paceValue || 1);
      const newDisplayDuration = app.sfp(newDuration);
      const displayStartTime = times.displayStartTime;

      this.setNewDuration(newDuration);
      this.setTimes({
        duration: newDuration,
        displayDuration: newDisplayDuration,
        displayEndTime: displayStartTime + newDisplayDuration,
      });

      this.$triggerAnalytics('assetEditor.pace', {
        newPace: 'x' + (paceValue || 1),
      });
    },

    changeDuration(duration) {
      const times = this.assetTimes;
      const newTimes = {
        displayDuration: duration,
        displayEndTime: app.sfp(times.displayStartTime + duration),
      };

      if (this.isVideoFragment) {
        newTimes.endTime = app.sfp(
          times.startTime + duration * this.playbackRate
        );
      }

      this.setNewDuration(duration);
      this.setTimes(newTimes);
    },
  },
};
</script>

<style lang="scss">
.wt-editor-top-bar-inner {
  height: 45px;
  padding-bottom: 5px;
  margin-top: unset;
  display: flex;
  align-items: center;
  font-size: 14px;

  .-pace-dropdown {
    margin-left: 10px;
  }

  .-right-side {
    width: 45%;
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    margin-left: auto;
  }

  .-left-side {
    width: 55%;
    display: inline-flex;
    align-items: center;
    margin-right: auto;

    .wt-fragment-duration {
      margin-left: 0;
      margin-right: 30px;
      height: 40px;
      display: flex;
      align-items: center;
      .wticon {
        margin-right: 5px;
        font-size: 16px;
        top: -1px;
      }
    }

    .-error {
      top: 8px;
      position: absolute;
      right: 237px;
    }

    &.-set-duration {
      .-label {
        margin-right: 5px;
      }
    }
  }

  .-toggle-ping-pong {
    font-size: 14px;
    display: inline-flex;
    justify-content: space-between;

    label {
      margin-left: 10px;
    }
  }
}
</style>
